import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MyContext } from '../../context';
import { createIframePreviewSession, getIframeConfig } from '../../utils/queries';
import './iframePreview.css';

// "View Store" test page for an iframe store (storefront_type 'iframe'). It
// stands in for the host app that embeds the storefront: the member form below
// is built from the store's iframe config (Developer > Iframe), the API signs
// those values into a JWT with the store's secret, and the iframe is pointed at
// <storefront><launchPath>?<tokenParam>=<JWT> -- the same handshake the real
// host app performs. Opened in a new tab via utils/launchStore.js.

const INPUT_CLASS = { integer: 'narrow', email: '' };

const initialValues = (fields, userProfile) =>
	Object.fromEntries(
		fields.map((field) => {
			const fromProfile =
				field.defaultFrom === 'userEmail'
					? userProfile?.email
					: field.defaultFrom === 'userFullName'
						? userProfile?.fullName
						: '';
			return [field.key, fromProfile || field.defaultValue || ''];
		}),
	);

// Ordered [group, fields[]] pairs, in first-appearance order -- one `.controls` row each.
const groupFields = (fields) =>
	fields.reduce((groups, field) => {
		const name = field.group || 'Member';
		const existing = groups.find(([g]) => g === name);
		if (existing) existing[1].push(field);
		else groups.push([name, [field]]);
		return groups;
	}, []);

export default function IframePreview() {
	const { userProfile, activeStoreId } = useContext(MyContext);
	const [searchParams] = useSearchParams();
	const storeId = Number(searchParams.get('storeId')) || activeStoreId;

	const [info, setInfo] = useState(null);
	const [values, setValues] = useState({});
	const [iframeSrc, setIframeSrc] = useState('');
	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState({ text: 'Initializing…', cls: '' });
	const launched = useRef(false);

	const setValue = (key) => (e) => setValues((current) => ({ ...current, [key]: e.target.value }));

	const launchSession = async (formValues) => {
		setLoading(true);
		setStatus({ text: 'Fetching signed token…', cls: '' });

		const result = await createIframePreviewSession(storeId, formValues);
		if (!result?.success) {
			setLoading(false);
			setStatus({ text: `Error: ${result?.error?.message || 'Failed to sign a session token.'}`, cls: 'error' });
			return;
		}

		setStatus({ text: 'Token issued · loading session…', cls: '' });
		setIframeSrc(result.data.launchUrl);
	};

	useEffect(() => {
		// Load config, then auto-launch once (StrictMode runs effects twice in dev).
		if (launched.current) return;
		launched.current = true;
		if (!storeId) {
			setStatus({ text: 'Error: no store selected.', cls: 'error' });
			return;
		}
		getIframeConfig(storeId).then((result) => {
			if (!result?.success) {
				setStatus({ text: `Error: ${result?.error?.message || 'Failed to load the store.'}`, cls: 'error' });
				return;
			}
			const data = result.data;
			document.title = `${data.storeName || 'Store'} (TEST)`;
			setInfo(data);
			if (data.storefrontType !== 'iframe') {
				setStatus({ text: 'Error: this is a standalone store, not an iframe store (Store - General).', cls: 'error' });
				return;
			}
			if (!data.secret?.configured) {
				setStatus({ text: 'Error: no JWT secret saved for this store yet (Developer > Iframe).', cls: 'error' });
				return;
			}
			const initial = initialValues(data.config.fields, userProfile);
			setValues(initial);
			launchSession(initial);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSubmit = (e) => {
		e.preventDefault();
		launchSession(values);
	};

	const handleIframeLoad = () => {
		if (!iframeSrc) return;
		setLoading(false);
		setStatus({ text: `Store loaded · ${new Date().toLocaleTimeString()}`, cls: 'ok' });
	};

	const groups = groupFields(info?.config?.fields || []);
	const canLaunch = info?.storefrontType === 'iframe' && info?.secret?.configured;

	return (
		<div className='iframe-preview'>
			<form id='member-form' onSubmit={handleSubmit}>
				{groups.map(([group, fields], index) => (
					<div className='controls' key={group}>
						<span className='controls-section'>{group}</span>
						{fields.map((field) => (
							<label key={field.key}>
								{field.label}{' '}
								<input
									id={`f-${field.key.replace(/\./g, '-')}`}
									type={field.type === 'integer' ? 'number' : field.type === 'email' ? 'email' : 'text'}
									{...(field.type === 'integer' ? { min: '0', step: '1' } : {})}
									required={field.required}
									value={values[field.key] ?? ''}
									onChange={setValue(field.key)}
									className={INPUT_CLASS[field.type] ?? ''}
								/>
							</label>
						))}
						{index === groups.length - 1 && (
							<>
								<button type='submit' disabled={loading || !canLaunch}>
									Launch session
								</button>
								<span id='status' className={status.cls}>
									{status.text}
								</span>
								{info?.storefrontUrl && <span className='storefront-url'>{info.storefrontUrl}</span>}
							</>
						)}
					</div>
				))}
				{groups.length === 0 && (
					<div className='controls'>
						<span id='status' className={status.cls}>
							{status.text}
						</span>
					</div>
				)}
			</form>

			{/* Store iframe */}
			<div className='page-body'>
				<div className='iframe-wrap'>
					{(loading || !iframeSrc) && (
						<div className='loading-overlay' id='overlay'>
							{loading || !status.cls ? 'Loading store…' : 'Store not loaded.'}
						</div>
					)}
					<iframe
						id='store'
						title={info?.storeName || 'Store'}
						src={iframeSrc || undefined}
						onLoad={handleIframeLoad}
					/>
				</div>
			</div>
		</div>
	);
}
